import { IUseOptions } from '@/interfaces/IUseOptions';
import { computed, Ref, toRaw } from 'vue';
import store             from '@/store';
import RedrawService     from '@/services/RedrawService';
import MapService        from '@/services/MapService';
import { IModalData } from '@/services/layers/IModalData';
import { IOpenWeather } from '@/interfaces/IOpenWeather';
import AssetIconService from '@/services/AssetIconService';
import TranslationService from '@/services/TranslationService';
import { LayerControlEnum } from '@/enums/LayerControlEnum';

const {secondsToCheckLastUpdate} = store.state;

const useOptions = (): IUseOptions => {
  const selectLocationMode: Ref<boolean> =
    computed(() => store.state.modes.selectLocation);
  const parkingsLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_PARKINGS]);
  const parkingZonesLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_PARKING_ZONES]);
  const parkingMetersLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_PARKING_METERS]);
  const parkingBoardsLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_PARKING_BOARDS]);
  const parkingSpotsLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_PARKING_SPOTS]);
  const parkingGroupsLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_PARKING_GROUPS]);
  const eventsLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_EVENTS]);
  const eventLayers: Ref<object> =
    computed(() => store.state.settings.mapEventLayers);
  const weatherStationsLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_WEATHER_STATIONS]);
  const bikeStationsLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_BIKE_STATIONS]);
  const vmsBoardsLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_VMS]);
  const radarBoardsLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_RADARS]);
  const cameras: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_CAM]);
  const eventList: Ref<boolean> =
    computed(() => store.state.otherModals.eventListModal.display);
  const openWeather: Ref<boolean> =
    computed(() => store.state.otherModals.openWeatherModal.display);
  const trafficLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_TRAFFIC]);
  const poiLayer: Ref<object> =
    computed(() => store.state.settings.mapPoiLayers);
  const trackLayer: Ref<boolean> =
    computed(() => store.state.settings.map[LayerControlEnum.SHOW_TRACKS]);
  const handleLocationModeChange = () => {
    store.commit('flipLocationMode');
  };
  const getCurrentTimestampInSeconds = (): number => Math.floor(Date.now() / 1000);
  const redrawMap = (): void => {
    const redrawService: RedrawService =
      new RedrawService(new MapService(toRaw(store.state.leafletMapInstance)));
    redrawService.redrawMap();
  };
  const disableTrackLayer = () => {
    // store.commit('disableTrackStartPointsLayer');
    store.commit('disableLayer', LayerControlEnum.SHOW_TRACKS);
    store.commit('switchOffSelectedStartPoint');
    store.commit('resetSelectedStartPointId');
    store.commit('updateTrackLines', []);
  };

  // const toggleParentlayers = (parentLayerName: string): void => {
  //   switch (parentLayerName) {
  //     case 'parkingsLayer':
  //       (store.state.settings.map[LayerControlEnum.SHOW_PARKING_GROUPS]
  //         || store.state.settings.map[LayerControlEnum.SHOW_PARKING_SPOTS]
  //         || store.state.settings.map[LayerControlEnum.SHOW_PARKING_ZONES]
  //         || store.state.settings.map[LayerControlEnum.SHOW_PARKING_METERS]
  //         || store.state.settings.map[LayerControlEnum.SHOW_PARKING_BOARDS]
  //       ) === true
  //         ? store.commit('enableLayer', LayerControlEnum.SHOW_PARKINGS)
  //         : store.commit('disableLayer', LayerControlEnum.SHOW_PARKINGS);
  //       break;
  //     default:
  //       break;
  //   }
  // };
  // const toggleSubLayers = (parentLayerName: string): void => {
  //   switch (parentLayerName) {
  //     case 'parkingsLayer':
  //       if (store.state.settings.map[LayerControlEnum.SHOW_PARKINGS] === true) {
  //         disableTrackLayer();
  //         store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_GROUPS);
  //         store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_SPOTS);
  //         store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_ZONES);
  //         store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_METERS);
  //         store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_BOARDS);
  //       } else {
  //         store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_GROUPS);
  //         store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_SPOTS);
  //         store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_ZONES);
  //         store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_METERS);
  //         store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_BOARDS);
  //       }
  //       break;
  //     default:
  //       break;
  //   }
  // };
  // all parkings layers
  const handleAllParkingsLayersChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKINGS);
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_SPOTS);
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_ZONES);
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_METERS);
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_BOARDS);

      Promise.all([
        store.dispatch('updateParkings'),
        store.dispatch('updateParkingGroups'),
        store.dispatch('updateParkingSpots'),
        store.dispatch('updateParkingZones'),
        store.dispatch('updateParkingMeters'),
        store.dispatch('updateParkingBoards'),
      ])
      .then(() => {
        redrawMap();
      });

    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKINGS);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_SPOTS);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_ZONES);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_METERS);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_BOARDS);
      redrawMap();
    }
    redrawMap();
  };
  // parkings
  const handleParkingsChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKINGS);

      if (
        store.state.parkings.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateParkings < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        Promise.all([
          store.dispatch('updateParkings'),
        ])
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKINGS);
      redrawMap();
    }
    redrawMap();
  };
  // parkingZone
  const handleParkingZonesChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_ZONES);
      if (
        store.state.parkingZones.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateParkingZones < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        store.dispatch('updateParkingZones')
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_ZONES);
      redrawMap();
    }
  };
  // parkingMeters
  const handleParkingMetersChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_METERS);
      if (
        store.state.parkingMeters.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateParkingMeters < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        Promise.all([
          store.dispatch('updateParkingMeters'),
          store.dispatch('updateParkingZones'),
        ])
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_METERS);
      redrawMap();
    }
  };
  // parkingBoards
  const handleParkingBoardsChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_BOARDS);
      if (
        store.state.parkingBoards.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateParkingBoards < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        store.dispatch('updateParkingBoards')
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_BOARDS);
      redrawMap();
    }
  };
  // parkingSpots
  const handleParkingSpotsChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_SPOTS);
      if (
        store.state.parkingSpots.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateParkingSpots < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        store.dispatch('updateParkingSpots')
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_SPOTS);
      redrawMap();
    }
  };
  // parkingGroups
  const handleParkingGroupsChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_PARKING_GROUPS);
      if (
        store.state.parkingGroups.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateParkingGroups < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        store.dispatch('updateParkingGroups')
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_GROUPS);
      redrawMap();
    }
  };
  const handleEventsChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableEventLayers');

      if (
        store.state.events.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateEvents < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        Promise.all([
          store.dispatch('updateEventCategories'),
          store.dispatch('updateEvents'),
        ])
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableEventLayers');
      redrawMap();
    }
  };

  const handleEventCategoryChange = (code: string, value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableEventCategoryLayer', code);
    } else {
      store.commit('disableEventCategoryLayer', code);
    }
    redrawMap();
  };
  // weatherStations
  const handleWeatherStationsChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_WEATHER_STATIONS);
      if (
        store.state.weatherStations.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateWeatherStations < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        store.dispatch('updateWeatherStations')
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_WEATHER_STATIONS);
      redrawMap();
    }
  };
  // Bike stations
  const handleBikeStationsChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_BIKE_STATIONS);
      if (
        store.state.bikeStations.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateBikeStations < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        store.dispatch('updateBikeStations')
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_BIKE_STATIONS);
      redrawMap();
    }
  };
  // VMS boards
  const handleVmsBoardsChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_VMS);
      if (
        store.state.vmsBoards.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateVmsBoards < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        store.dispatch('updateVmsBoards')
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_VMS);
      redrawMap();
    }
  };
  // Radar boards
  const handleRadarBoardsChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_RADARS);
      if (
        store.state.radarBoards.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateRadarBoards < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        store.dispatch('updateRadarBoards')
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_RADARS);
      redrawMap();
    }
  };
  // cameras all
  const handleCamerasChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_CAM);
      // store.commit('enableCamerasLayer');
      // store.commit('enableCctvFixedCamerasLayer');
      // store.commit('enableCctvPtzCamerasLayer');
      if (
        store.state.cameras.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateCameras < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        store.dispatch('updateCameras')
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_CAM);
      // store.commit('disableCamerasLayer');
      // store.commit('disableCctvFixedCamerasLayer');
      // store.commit('disableCctvPtzCamerasLayer');
      redrawMap();
    }
  };
  // traffic
  const handleTrafficChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enableLayer', LayerControlEnum.SHOW_TRAFFIC);

      if (
        store.state.traffic.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdateTraffic < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        Promise.all([
          store.dispatch('updateTraffic'),
          store.dispatch('updateTrafficPoints'),
        ])
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disableLayer', LayerControlEnum.SHOW_TRAFFIC);
      redrawMap();
    }
  };
  // POI
  const handlePoiChange = (value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enablePoiLayers');

      if (
        store.state.poiPoints.features.length
        && getCurrentTimestampInSeconds() - store.state.requestsToApi.lastUpdatePoiLayers < secondsToCheckLastUpdate
      ) {
        redrawMap();
      } else {
        Promise.all([
          store.dispatch('updatePoiLayers'),
          store.dispatch('updatePoiPoints'),
        ])
        .then(() => {
          redrawMap();
        });
      }
    } else {
      store.commit('disablePoiLayers');
      redrawMap();
    }
  };
  const handlePoiCategoryChange = (code: string, value: boolean) => {
    if (value) {
      disableTrackLayer();
      store.commit('enablePoiCategoryLayer', code);
    } else {
      store.commit('disablePoiCategoryLayer', code);
    }
    redrawMap();
  };
  // track layers 
  const handleTrackChange = (value: boolean) => {
    if (value) {
      store.commit('enableLayer', LayerControlEnum.SHOW_TRACKS);
      // disable other layers
      store.commit('disableLayer', LayerControlEnum.SHOW_EVENTS);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKINGS);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_GROUPS);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_SPOTS);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_ZONES);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_METERS);
      store.commit('disableLayer', LayerControlEnum.SHOW_PARKING_BOARDS);
      store.commit('disableLayer', LayerControlEnum.SHOW_VMS);
      store.commit('disableLayer', LayerControlEnum.SHOW_RADARS);
      store.commit('disableLayer', LayerControlEnum.SHOW_CAM);
      store.commit('disableLayer', LayerControlEnum.SHOW_CCTV_FIXED_CAMERAS);
      store.commit('disableLayer', LayerControlEnum.SHOW_CCTV_PTZ_CAMERAS);
      store.commit('disableLayer', LayerControlEnum.SHOW_TRAFFIC);
      store.commit('disableLayer', LayerControlEnum.SHOW_WEATHER_STATIONS);
      store.commit('disablePoiLayers');

    } else {
      disableTrackLayer();
    }
    redrawMap();
  };

  // event list
  const handleEventList = (value: boolean) => {
    if (value) {
      store.commit('openEventListModal');
    } else {
      store.commit('closeEventListModal');
    }
  };
  // open weather 
  const handleOpenWeather = (value: boolean) => {
    // store.commit('openOpenWeatherModal');
    const openWeatherData: Ref<IOpenWeather> = computed((): IOpenWeather => store.getters.getOpenWeather);
    const lastUpdate = openWeatherData.value.date ? openWeatherData.value.date : '';
    const modalIconUrl: string = AssetIconService.getSvgModalIcon('modal_weather_station');

    const modalData: IModalData = {
      title: `Chorzów`,
      content: '',
      navigationPoint: undefined,
      lastUpdate,
      icon: modalIconUrl,
      templateName: 'TemplateModalOpenWeather',
      metaData: {},
    };

    store.commit('displayModal', modalData);
  };

  return {
    selectLocationMode,
    parkingsLayer,
    parkingZonesLayer,
    parkingMetersLayer,
    parkingBoardsLayer,
    parkingSpotsLayer,
    parkingGroupsLayer,
    eventsLayer,
    eventLayers,
    weatherStationsLayer,
    bikeStationsLayer,
    vmsBoardsLayer,
    radarBoardsLayer,
    cameras,
    // camerasFixed,
    // camerasPtz,
    eventList,
    openWeather,
    trafficLayer,
    poiLayer,
    trackLayer,
    handleLocationModeChange,
    handleAllParkingsLayersChange,
    handleParkingsChange,
    handleParkingZonesChange,
    handleParkingMetersChange,
    handleParkingSpotsChange,
    handleParkingGroupsChange,
    handleParkingBoardsChange,
    handleEventsChange,
    handleEventCategoryChange,
    handleWeatherStationsChange,
    handleBikeStationsChange,
    handleVmsBoardsChange,
    handleRadarBoardsChange,
    handleCamerasChange,
    handleEventList,
    handleOpenWeather,
    handleTrafficChange,
    handlePoiChange,
    handlePoiCategoryChange,
    handleTrackChange,
  };
};

export default useOptions;
